<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="all-brands">
    <div class="wrapper">
      <div v-if="content.logoTitle" class="all-brands__title-wrapper">
        <h1 class="all-brands__title h3" v-html="content.logoTitle"></h1>
      </div>

      <Brands :content="content.logos"></Brands>

      <nav aria-label="letters" class="all-brands__nav">
        <ol class="all-brands__nav__list">
          <li
            v-for="(item, index) in content.nav"
            :key="`letters-${index}`"
            class="all-brands__nav__item"
            :class="{ 'is-disabled': item.hasBrand }"
          >
            <button
              :disabled="!item.hasBrand"
              @click="onAnchorClick(`#${item.title}`)"
            >
              <span v-html="item.title"></span>
            </button>
          </li>
        </ol>
      </nav>

      <div class="all-brands__grid__items">
        <div
          v-for="item in content.items"
          :id="item.title"
          :key="`brand-list-${item.title}`"
          class="all-brands__grid__item"
        >
          <div class="all-brands__grid__item__title">{{ item.title }}</div>
          <ul class="all-brands__grid__item__list">
            <li
              v-for="(brand, index) in item.items"
              :key="`brand-name-${index}`"
            >
              <RouterLink
                class="all-brands__grid__item__list__link"
                :to="brand.url"
                >{{ brand.title }}</RouterLink
              >
            </li>
          </ul>
        </div>
      </div>
    </div>

    <BigCta :content="content.bigCta"></BigCta>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import BigCta from '@/components/global/BigCta.vue'
import Brands from '@/components/global/Brands.vue'
import { prefersReducedMotion } from '@/utils/motion'

// Props
const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const current = ref(props.content.items[0].title)

// Scroll smoothly to letter
const onAnchorClick = (link: string) => {
  const el = document.querySelector(link)

  current.value = link

  if (!el) {
    return
  }

  const { top } = el.getBoundingClientRect()

  window.scrollTo({
    top: top + window.scrollY - 10,
    left: 0,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  })
}
</script>

<style lang="scss" scoped>
.all-brands {
  padding-top: $header-height-mobile;

  :deep(.brands-wrapper) {
    padding-block: 0;

    @include mq($until: xxxl) {
      width: 100%;
    }
  }

  :deep(.brands) {
    display: flex;
    margin-top: $spacing * 2;
    gap: 0;

    @include mq(s) {
      gap: 0;
    }

    @include mq(xl) {
      gap: 0;
    }
  }

  :deep(.brands__card) {
    max-width: none;
    padding: 1.6rem;
    border: 0.1rem solid $c-grey-light;
  }

  @include mq(l) {
    padding-top: $header-height;
  }
}

.all-brands__title-wrapper {
  display: flex;
  margin-top: 10rem;
}

.all-brands__title {
  @extend %fraunces-spec;
  @extend %fw-light;

  margin: auto;
  color: $c-green-abr;
  font-size: 3.8rem;
  line-height: 1;
}

.all-brands__nav__list {
  @extend %list-nostyle;

  display: flex;
  overflow: auto;
  width: fit-content;
  max-width: 90vw;
  gap: 0.2rem;
  margin: $spacing * 3 auto;
}

.all-brands__nav__item {
  button {
    width: $spacing * 2;
    height: $spacing * 2;
    cursor: pointer;
    background: $c-white;
    border: 0.1rem solid rgba(171, 171, 171, 30%);
    border-radius: 0.8rem;
    backdrop-filter: blur(10px);
    transition: all 0.2s ease-in-out;

    &:disabled {
      background: $c-gray-lighter;
      opacity: 0.4;
    }

    &:not(:disabled) {
      &:hover {
        color: $c-white;
        background: $c-green-abr;
      }
    }
  }
}

.all-brands__grid__items {
  position: relative;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  margin-top: $spacing * 4;
  margin-bottom: $spacing * 8;
  gap: $spacing * 2;

  @include mq(s) {
    grid-template-columns: repeat(3, 1fr);
  }

  @include mq(xl) {
    grid-template-columns: repeat(5, 1fr);
  }
}

.all-brands__grid__item__list {
  @extend %list-nostyle;
}

.all-brands__grid__item__title {
  padding-bottom: 0.8rem;
  color: $c-green-abr;
  font-size: 2rem;
  font-weight: 600;
  line-height: 2.4rem;
  border-bottom: 0.1rem solid $c-gray-lighter;
}

.all-brands__grid__item__list__link {
  padding-block: 0.3rem;
  color: $c-grey-dark;
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.8rem;

  &:hover {
    color: $c-green-abr;
  }
}
</style>
